import { useState, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, AtSign, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface Profile {
  id: string;
  full_name: string;
  email: string;
}

interface CommentComposerProps {
  leadId?: string;
  jobId?: string;
  orgId: string;
  onCommentAdded?: () => void;
}

export function CommentComposer({
  leadId,
  jobId,
  orgId,
  onCommentAdded,
}: CommentComposerProps) {
  const { user } = useAuth();
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState("");
  const [mentionPosition, setMentionPosition] = useState(0);
  const [orgUsers, setOrgUsers] = useState<Profile[]>([]);
  const [mentionedUsers, setMentionedUsers] = useState<string[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    loadOrgUsers();
  }, [orgId]);

  const loadOrgUsers = async () => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .eq("org_id", orgId)
        .neq("id", user?.id);

      if (error) throw error;
      setOrgUsers(data || []);
    } catch (error: any) {
      console.error("Error loading org users:", error);
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const cursorPosition = e.target.selectionStart;

    setContent(value);

    // Check for @ mentions
    const textBeforeCursor = value.slice(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf("@");

    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1);
      if (!textAfterAt.includes(" ")) {
        setMentionSearch(textAfterAt);
        setMentionPosition(lastAtIndex);
        setShowMentions(true);
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
  };

  const insertMention = (profile: Profile) => {
    const beforeMention = content.slice(0, mentionPosition);
    const afterMention = content.slice(mentionPosition + mentionSearch.length + 1);
    const newContent = `${beforeMention}@${profile.full_name} ${afterMention}`;

    setContent(newContent);
    setShowMentions(false);

    if (!mentionedUsers.includes(profile.id)) {
      setMentionedUsers([...mentionedUsers, profile.id]);
    }

    textareaRef.current?.focus();
  };

  const handleSubmit = async () => {
    if (!content.trim() || !user) return;

    setSending(true);
    try {
      // Create comment
      const { data: comment, error: commentError } = await supabase
        .from("comments")
        .insert({
          content: content.trim(),
          user_id: user.id,
          org_id: orgId,
          lead_id: leadId,
          job_id: jobId,
        })
        .select()
        .single();

      if (commentError) throw commentError;

      // Create mentions
      if (mentionedUsers.length > 0) {
        const mentionRecords = mentionedUsers.map((userId) => ({
          comment_id: comment.id,
          user_id: userId,
        }));

        const { error: mentionError } = await supabase
          .from("mentions")
          .insert(mentionRecords);

        if (mentionError) throw mentionError;

        // Create notifications for mentioned users
        const notificationRecords = mentionedUsers.map((userId) => ({
          user_id: userId,
          org_id: orgId,
          type: "mention",
          title: "You were mentioned",
          message: `${user.email} mentioned you in a comment`,
          comment_id: comment.id,
          lead_id: leadId,
          job_id: jobId,
          priority: "normal",
        }));

        const { error: notifError } = await supabase
          .from("notifications")
          .insert(notificationRecords);

        if (notifError) console.error("Error creating notifications:", notifError);
      }

      setContent("");
      setMentionedUsers([]);
      toast.success("Comment posted");
      onCommentAdded?.();
    } catch (error: any) {
      console.error("Error posting comment:", error);
      toast.error("Failed to post comment");
    } finally {
      setSending(false);
    }
  };

  const filteredUsers = orgUsers.filter((profile) =>
    profile.full_name?.toLowerCase().includes(mentionSearch.toLowerCase()) ||
    profile.email?.toLowerCase().includes(mentionSearch.toLowerCase())
  );

  return (
    <div className="space-y-2">
      <div className="relative">
        <Textarea
          ref={textareaRef}
          value={content}
          onChange={handleTextChange}
          placeholder="Write a comment... Use @ to mention team members"
          className="min-h-[80px] resize-none"
          disabled={sending}
        />

        {showMentions && filteredUsers.length > 0 && (
          <div className="absolute bottom-full mb-2 left-0 right-0 z-50">
            <div className="bg-popover border rounded-md shadow-lg max-h-[200px] overflow-auto">
              <Command>
                <CommandList>
                  <CommandGroup heading="Mention user">
                    {filteredUsers.map((profile) => (
                      <CommandItem
                        key={profile.id}
                        onSelect={() => insertMention(profile)}
                        className="cursor-pointer"
                      >
                        <AtSign className="h-4 w-4 mr-2" />
                        <div>
                          <div className="font-medium">{profile.full_name}</div>
                          <div className="text-xs text-muted-foreground">
                            {profile.email}
                          </div>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </div>
          </div>
        )}
      </div>

      {mentionedUsers.length > 0 && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <AlertCircle className="h-3 w-3" />
          <span>
            {mentionedUsers.length} user{mentionedUsers.length > 1 ? "s" : ""} will be
            notified
          </span>
        </div>
      )}

      <div className="flex justify-end">
        <Button onClick={handleSubmit} disabled={!content.trim() || sending} size="sm">
          <Send className="h-4 w-4 mr-2" />
          {sending ? "Sending..." : "Send"}
        </Button>
      </div>
    </div>
  );
}
